import React, { useState, useEffect } from 'react';
import './App.css';
import ImageUpload from './components/ImageUpload';
import PatternPreview from './components/PatternPreview';
import ConfigurationPanel from './components/ConfigurationPanel';

interface PatternData {
  grid: number[][];
  colors: Array<{
    index: number;
    rgb: number[];
    hex: string;
  }>;
  dimensions: {
    width: number;
    height: number;
  };
  scanlines: any[];
  yarn_requirements: any;
  gauge_info: any;
  yarn_weight: number;
  difficulty: string;
  estimated_time: any;
  initial_palette?: Array<{
    index: number;
    rgb: number[];
    hex: string;
  }>;
}

interface ProcessingConfig {
  num_colors: number;
  target_width: number;
  yarn_weight: number;
  padding_type: 'none' | 'stitches' | 'inches' | 'auto';
  padding_horizontal: number;
  padding_vertical: number;
  blanket_size: string;
  custom_width: number;
  custom_height: number;
}

function App() {
  const [patternData, setPatternData] = useState<PatternData | null>(null);
  const [processing, setProcessing] = useState(false);
  const [initialPalette, setInitialPalette] = useState<Array<{ index: number; rgb: number[]; hex: string; }> | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [lastUsedConfig, setLastUsedConfig] = useState<ProcessingConfig | null>(null);
  const [config, setConfig] = useState<ProcessingConfig>({
    num_colors: 4,
    target_width: 200,
    yarn_weight: 4,
    padding_type: 'auto',
    padding_horizontal: 0,
    padding_vertical: 0,
    blanket_size: 'throw',
    custom_width: 50,
    custom_height: 60
  });


  const handlePatternGenerated = (data: PatternData, file?: File) => {
    setPatternData(data);
    setProcessing(false);
    // Store the uploaded file and config for recalculation
    if (file) {
      setUploadedFile(file);
      setLastUsedConfig({ ...config });
    }
    // Extract initial palette if available
    if (data && data.initial_palette) {
      setInitialPalette(data.initial_palette);
    }
  };

  const handleProcessingStart = () => {
    setProcessing(true);
    setPatternData(null);
    setInitialPalette(null);
  };

  const handleConfigChange = (newConfig: Partial<ProcessingConfig>) => {
    setConfig(prev => ({ ...prev, ...newConfig }));
  };

  const recalculatePattern = async () => {
    if (!uploadedFile) return;

    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);
      formData.append('num_colors', config.num_colors.toString());
      formData.append('target_width', config.target_width.toString());
      formData.append('yarn_weight', config.yarn_weight.toString());
      formData.append('padding_type', config.padding_type);
      formData.append('padding_horizontal', config.padding_horizontal.toString());
      formData.append('padding_vertical', config.padding_vertical.toString());
      formData.append('blanket_size', config.blanket_size);
      formData.append('custom_width', config.custom_width.toString());
      formData.append('custom_height', config.custom_height.toString());
      
      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
      });
      
      const result = await response.json();
      
      if (result.status === 'completed') {
        setPatternData(result.pattern_data);
        setLastUsedConfig({ ...config });
        if (result.pattern_data.initial_palette) {
          setInitialPalette(result.pattern_data.initial_palette);
        }
      } else {
        throw new Error('Pattern generation failed');
      }
    } catch (error: any) {
      console.error('Recalculation failed:', error);
      alert('Failed to recalculate pattern. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  // Check if settings have changed since last calculation
  const hasConfigChanged = lastUsedConfig && uploadedFile && (
    lastUsedConfig.num_colors !== config.num_colors ||
    lastUsedConfig.target_width !== config.target_width ||
    lastUsedConfig.yarn_weight !== config.yarn_weight ||
    lastUsedConfig.padding_type !== config.padding_type ||
    lastUsedConfig.padding_horizontal !== config.padding_horizontal ||
    lastUsedConfig.padding_vertical !== config.padding_vertical ||
    lastUsedConfig.blanket_size !== config.blanket_size ||
    lastUsedConfig.custom_width !== config.custom_width ||
    lastUsedConfig.custom_height !== config.custom_height
  );

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-content">
          <div className="brand-section">
            <div className="etsy-branding">
              <img 
                src="/knotsoflove.jpg" 
                alt="Knotz of Love Crochet" 
                className="shop-logo"
              />
              <div className="shop-info">
                <h2 className="shop-name">Knotz of Love Crochet</h2>
                <div className="shop-links">
                  <a 
                    href="https://www.etsy.com/shop/knotzoflovecrochet/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="etsy-link"
                  >
                    Visit My Etsy Shop →
                  </a>
                  <a 
                    href="https://www.buymeacoffee.com/KnotsOfLove" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="donation-button"
                  >
                    🧶 Buy me a skein
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="app-title-section">
            <h1>Pattern Generator</h1>
            <p>Transform your images into beautiful crochet patterns</p>
          </div>
        </div>
      </header>
      
      <main className="App-main">
        <div className="container">
          <div className="upload-section">
            <ImageUpload
              config={config}
              onPatternGenerated={handlePatternGenerated}
              onProcessingStart={handleProcessingStart}
              processing={processing}
            />
            
            <ConfigurationPanel
              config={config}
              onChange={handleConfigChange}
              disabled={processing}
              hasConfigChanged={hasConfigChanged || false}
              onRecalculate={recalculatePattern}
            />
          </div>
          
          {patternData && (
            <div className="preview-section">
              <PatternPreview patternData={patternData} />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default App;
